import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import c from 'classnames';
import { Button, Icon, Typography } from '#components';
import styles from './Modal.module.css';

export interface ModalProps {
  children: ReactNode;
  className?: string;
  collapse?: boolean;
  footer?: ReactNode;
  label?: React.ReactNode;
  labelClassName?: string;
  isDismissable?: boolean; // Defaults to true
  showFloatingX?: boolean;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  preToggle?: boolean;
  showHeader?: boolean;
  title?: string;
  hasBodyPadding?: boolean; // Defaults to true, set false to remove body padding
  closeOnBackdropClick?: boolean; // Defaults to true, set false to prevent backdrop dismiss
}

export function Modal({
  children,
  className = '',
  collapse,
  footer,
  isDismissable = true,
  showFloatingX = false,
  isOpen: controlledIsOpen,
  label,
  labelClassName,
  onOpen,
  onClose,
  preToggle,
  showHeader,
  title,
  hasBodyPadding = true,
  closeOnBackdropClick = true,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (preToggle) {
      setIsOpen(true);
      onOpen?.();
    }
  }, [onOpen, preToggle]);

  useEffect(() => {
    if (controlledIsOpen !== undefined) {
      setIsOpen(controlledIsOpen);
    }
  }, [controlledIsOpen]);

  const toggle = () => {
    if (onOpen && !isOpen) {
      onOpen();
    }
    if (onClose && isOpen) {
      onClose();
    }
    setIsOpen(!isOpen);
  };
  useEffect(() => {
    setIsMounted(true); // render modal client side only
  }, []);

  // Handle Escape key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        console.log('Escape pressed');
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Focus trapping and restoration
  useEffect(() => {
    if (isOpen) {
      onOpen?.();
      previousFocus.current = document.activeElement as HTMLElement;
      // Basic focus trap - focus the modal itself initially
      // A more robust solution would cycle through focusable elements
      modalRef.current?.focus();

      // Trap focus within the modal
      const handleFocusTrap = (event: FocusEvent) => {
        if (
          modalRef.current &&
          !modalRef.current.contains(event.target as Node)
        ) {
          modalRef.current.focus();
        }
      };
      document.addEventListener('focusin', handleFocusTrap);
      return () => {
        document.removeEventListener('focusin', handleFocusTrap);
        previousFocus.current?.focus(); // Restore focus
      };
    }
  }, [isOpen, onOpen]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = ''; // Ensure scroll is restored on unmount
    };
  }, [isOpen]);

  // Framer Motion variants for backdrop
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  // Framer Motion variants for dialog
  const dialogVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 }, // Ensure exit matches hidden for smooth transition
  };

  return (
    <>
      {!!label && (
        <div role='button' className={labelClassName} onClick={toggle}>
          {label}
        </div>
      )}
      {isMounted &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <motion.div
                className={styles.modalBackdrop}
                onClick={closeOnBackdropClick ? onClose : undefined}
                role='presentation'
                initial='hidden'
                animate='visible'
                exit='hidden'
                variants={backdropVariants}
                transition={{ duration: 0.15 }}
              >
                <motion.div
                  ref={modalRef}
                  className={`${styles.modalDialog} ${className}`}
                  role='dialog'
                  aria-modal='true'
                  aria-labelledby='modal-title'
                  tabIndex={-1}
                  onClick={e => e.stopPropagation()}
                  initial='hidden'
                  animate='visible'
                  exit='exit'
                  variants={dialogVariants}
                  transition={{ duration: 0.15 }}
                >
                  {showHeader ? (
                    <div className={styles.modalHeader}>
                      {title ? (
                        <Typography
                          id='modal-title'
                          variant='heading-m'
                          className={styles.modalTitle}
                        >
                          {title}
                        </Typography>
                      ) : (
                        <div></div>
                      )}
                      {isDismissable ? (
                        <Button
                          type='button'
                          variant='wrapper'
                          onClick={onClose}
                          aria-label='Close'
                        >
                          <Icon
                            name={collapse ? 'collapse' : 'closeSm'}
                            size='m'
                          />
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                  <div
                    className={c(
                      styles.modalBody,
                      !hasBodyPadding && styles.modalBodyNoPadding,
                    )}
                  >
                    {showFloatingX && (
                      <Button
                        type='button'
                        variant='wrapper'
                        onClick={onClose}
                        aria-label='Close'
                        className={styles.floatingCloseButton}
                      >
                        <Icon name='closeSm' size='m' />
                      </Button>
                    )}
                    {children}
                  </div>
                  {footer && <div className={styles.modalFooter}>{footer}</div>}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
}
